// routes/Entry.jsx — 첫 진입 (ux §3.1).
//
// QA v2 의 **D-3(차단)**: Space 가 0개인 새 사람에게 `참여 주소로 신청하기` 진입로가 없어
// 초대받은 사람이 막혔다. 그래서 이 화면은 참여 신청·새 Space 만들기를 **목록의 유무와 무관하게
// 항상** 같은 자리에 둔다. 빈 상태가 진입로를 숨기는 구조를 만들지 않는다.

import { useRef } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { command, read } from '../api/client.js';
import { useCommand, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import { withParticle } from '../ui/korean-particle.js';
import { Button, Field, Form, Input, List, ListRow, Notice, Panel } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';

export function Entry() {
  const session = useSession();
  const [, navigate] = useLocation();

  const joinRequests = useResource('entry:join-requests', () =>
    read('my-join-requests', { nodeId: session.nodeId }),
  );

  // membership 상태와 Space 상태는 다르다. 보관된 Space를 목록에서 잃지 않되,
  // 새 기록을 남길 수 있는 Space와 섞어 "들어갈 수 있다"고 오해시키지 않는다.
  const joined = session.spaces.filter((s) => s.state === 'active');
  const active = joined.filter((s) => s.spaceState !== 'archived');
  const archived = joined.filter((s) => s.spaceState === 'archived');

  return (
    <main class="plain">
      <div class="plain__inner">
        <header class="plain__head">
          <h1 class="t-display">참여 중인 공간</h1>
          <p class="t-meta">{withParticle(session.node.displayName, 'direction')} 들어와 있습니다.</p>
        </header>

        <Panel title="지금 들어갈 수 있는 공간">
          {active.length === 0 ? (
            <Empty title="아직 참여 중인 공간이 없습니다.">
              아래에서 공간 코드로 신청하거나, 새 공간을 만들 수 있습니다.
            </Empty>
          ) : (
            <List>
              {active.map((s) => {
                const openable = s.spaceState !== 'archived';
                return (
                  <ListRow key={s.spaceId} href={openable ? `/s/${s.spaceId}` : undefined}>
                    <span class="grow">
                      <span class="t-body-strong">{s.name ?? '이름을 아직 읽지 못했습니다'}</span>
                    </span>
                    <span class="t-meta">{roleLabel(s.role)}</span>
                    {s.spaceState === 'archived' ? <span class="t-meta">보관됨</span> : null}
                  </ListRow>
                );
              })}
            </List>
          )}
        </Panel>

        {archived.length > 0 ? (
          <Panel title="보관된 공간">
            <List>
              {archived.map((s) => (
                <ListRow key={s.spaceId} href={`/s/${s.spaceId}`}>
                  <span class="grow"><span class="t-body-strong">{s.name ?? '이름을 아직 읽지 못했습니다'}</span></span>
                  <span class="t-meta">기록 읽기</span>
                </ListRow>
              ))}
            </List>
          </Panel>
        ) : null}

        {/* 진입로는 목록의 유무와 무관하게 항상 여기 있다 (D-3). */}
        <JoinBySpaceId nodeId={session.nodeId} />
        {/* 새 공간의 레일·전환 컨트롤은 세션의 spaces 를 읽는다. 재부트스트랩 없이 이동하면
            방금 만든 공간이 전환 목록에 빈 칸으로 남는다(E-40). */}
        <CreateSpace
          nodeId={session.nodeId}
          onCreated={async (id) => {
            await session.reload();
            navigate(`/s/${id}`);
          }}
        />

        <Panel title="보낸 참여 신청">
          <Resource resource={joinRequests} loadingLabel="신청 내역을 읽는 중" compact>
            {(data) =>
              (data.joinRequests ?? []).length === 0 ? (
                <Empty title="보낸 신청이 없습니다." />
              ) : (
                <List>
                  {data.joinRequests.map((r) => <SentJoinRequest key={r.joinRequestId} request={r} nodeId={session.nodeId} />)}
                </List>
              )
            }
          </Resource>
        </Panel>
      </div>
    </main>
  );
}

function SentJoinRequest({ request, nodeId }) {
  const cancel = useCommand(
    (params, idempotencyKey) => command('join_cancel', params, { nodeId, idempotencyKey }),
    { invalidates: ['entry'] },
  );
  const spaceId = request.space?.spaceId;
  const canCancel = request.state === 'pending' && spaceId && request.joinRequestId;

  return (
    <ListRow>
      <span class="grow">
        {/* 이름을 주면 이름을, 안 주면 없다고 말한다. 아직 멤버가 아닌 Space의 다른
            정보를 억지로 읽어 붙이지 않는다. */}
        <span class="t-body-strong">{request.space?.name ?? '승인 전에는 이름이 보이지 않습니다'}</span>
      </span>
      <span class="t-meta">{joinStateLabel(request.state)}</span>
      {canCancel ? (
        <Button
          size="sm"
          tone="ghost"
          busy={cancel.pending}
          onClick={() => cancel.run({ spaceId, joinRequestId: request.joinRequestId })}
        >
          신청 취소
        </Button>
      ) : null}
      {cancel.error ? <span class="t-meta">취소하지 못했습니다.</span> : null}
    </ListRow>
  );
}

function JoinBySpaceId({ nodeId }) {
  const spaceIdRef = useRef(null);
  const messageRef = useRef(null);
  const join = useCommand(
    (params, idempotencyKey) => command('join_request', params, { nodeId, idempotencyKey }),
    { invalidates: ['entry'] },
  );

  return (
    <Panel title="공간 코드로 참여 신청">
      <Form
        busy={join.pending}
        onSubmit={async () => {
          const id = spaceIdRef.current?.value?.trim() ?? '';
          const message = messageRef.current?.value?.trim() ?? '';
          if (id === '') return;
          const result = await join.run({ spaceId: id, message: message || undefined });
          if (result.ok) {
            if (spaceIdRef.current) spaceIdRef.current.value = '';
            if (messageRef.current) messageRef.current.value = '';
          }
        }}
      >
        <div class="stack">
          <Field
            label="공간 코드"
            htmlFor="joinSpaceId"
            required
            hint="공간을 운영하는 사람에게 받은 코드를 붙여 넣습니다. 서버가 이름을 제공하면 보낸 신청 목록에서 확인할 수 있습니다."
          >
            <Input
              id="joinSpaceId"
              name="spaceId"
              ref={spaceIdRef}
            />
          </Field>
          <Field label="한마디 (선택)" htmlFor="joinMessage">
            <Input
              id="joinMessage"
              name="message"
              ref={messageRef}
            />
          </Field>

          {join.error ? <Notice tone="danger" title={describeError(join.error).message} /> : null}
          {join.done ? (
            <Notice tone="accepted" title="신청을 보냈습니다. 운영자가 승인하면 목록에 나타납니다." />
          ) : null}

          <div class="row">
            <Button kind="submit" busy={join.pending}>
              {join.pending ? '보내는 중' : '참여 신청'}
            </Button>
          </div>
        </div>
      </Form>
    </Panel>
  );
}

function CreateSpace({ nodeId, onCreated }) {
  const nameRef = useRef(null);
  const create = useCommand(
    (params, idempotencyKey) => command('space_create', params, { nodeId, idempotencyKey }),
    { invalidates: ['entry'] },
  );

  return (
    <Panel title="새 공간 만들기">
      <Form
        busy={create.pending}
        onSubmit={async () => {
          const name = nameRef.current?.value?.trim() ?? '';
          if (name === '') return;
          const result = await create.run({ name });
          const spaceId = result.data?.space?.spaceId ?? result.data?.spaceId;
          if (result.ok && spaceId) onCreated?.(spaceId);
        }}
      >
        <div class="stack">
          <Field label="공간 이름" htmlFor="newSpaceName" required>
            <Input
              id="newSpaceName"
              name="name"
              ref={nameRef}
            />
          </Field>

          {create.error ? (
            <Notice tone="danger" title={describeError(create.error).message} />
          ) : null}

          <div class="row">
            <Button kind="submit" busy={create.pending}>
              {create.pending ? '만드는 중' : '공간 만들기'}
            </Button>
          </div>
        </div>
      </Form>
    </Panel>
  );
}

function roleLabel(role) {
  if (role === 'owner') return '오너';
  if (role === 'steward') return '관리';
  return '참여';
}

function joinStateLabel(state) {
  switch (state) {
    case 'pending':
      return '승인 대기';
    case 'approved':
      return '승인됨';
    case 'rejected':
      return '거절됨';
    case 'cancelled':
      return '취소함';
    default:
      // 모르는 값을 「승인 대기」로 낙관하지 않는다. 그대로 보여 준다.
      return state;
  }
}
